import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { setDemoUser, clearDemoUser } from '../services/supabaseService';

const AuthContext = createContext();

const DEMO_USER = {
  id: 'demo-user-id',
  email: 'demo@omrahajjmanager.com',
  user_metadata: { full_name: 'Demo Agency', role: 'agency_admin' },
  isDemo: true,
};

const DEMO_PROFILE = {
  full_name: 'Demo Agency',
  role: 'agency_admin',
  agency_name: 'OmraHajjManager Demo',
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Safely load user profile — never throws, always resolves
  const loadUserProfile = async (userId) => {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.maybeSingle();

      if (!error && data) {
        setProfile(data);
      } else {
        // Profile table may not exist yet or row not found — that's OK
        setProfile(null);
      }
    } catch {
      setProfile(null);
    }
  };

  useEffect(() => {
    let mounted = true;

    // Get initial session
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      if (!mounted) return;
      const sessionUser = session?.user ?? null;
      setUser(sessionUser);
      if (sessionUser) {
        loadUserProfile(sessionUser?.id)?.finally(() => {
          if (mounted) setLoading(false);
        });
      } else {
        setLoading(false);
      }
    })?.catch(() => {
      if (mounted) setLoading(false);
    });

    // Listen for auth state changes
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((_event, session) => {
      if (!mounted) return;
      const sessionUser = session?.user ?? null;
      setUser(sessionUser);
      if (sessionUser) {
        loadUserProfile(sessionUser?.id)?.finally(() => {
          if (mounted) setLoading(false);
        });
      } else {
        setProfile(null);
        if (mounted) setLoading(false);
      }
    });

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  const signInDemo = () => {
    setUser(DEMO_USER);
    setProfile(DEMO_PROFILE);
    setLoading(false);
    setDemoUser(DEMO_USER);
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase?.auth?.signInWithPassword({ email, password });
    if (error) throw error;
    // Immediately set user and loading=false so ProtectedRoute passes right away
    if (data?.user) {
      setUser(data?.user);
      setLoading(false);
      loadUserProfile(data?.user?.id);
    }
    return data;
  };

  const signUp = async (email, password, metadata = {}) => {
    const { data, error } = await supabase?.auth?.signUp({
      email,
      password,
      options: { data: metadata },
    });
    if (error) throw error;
    // Auto-confirmation is enabled — user is immediately available
    if (data?.user) {
      setUser(data?.user);
      loadUserProfile(data?.user?.id);
    }
    return data;
  };

  const signOut = async () => {
    if (user?.isDemo) {
      setUser(null);
      setProfile(null);
      clearDemoUser();
      return;
    }
    const { error } = await supabase?.auth?.signOut();
    if (error) throw error;
    setUser(null);
    setProfile(null);
    clearDemoUser();
  };

  const resetPassword = async (email) => {
    const { error } = await supabase?.auth?.resetPasswordForEmail(email, {
      redirectTo: `https://omramanager.ma/login`,
    });
    if (error) throw error;
  };

  const updateUserMetadata = async (metadata) => {
    const { data, error } = await supabase?.auth?.updateUser({ data: metadata });
    if (error) throw error;
    return data;
  };

  const value = {
    user,
    profile,
    loading,
    signIn,
    signInDemo,
    signUp,
    signOut,
    resetPassword,
    updateUserMetadata,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
